import React from 'react';
import {removeMetaMarkByUid} from '../../../../../components/prosemirror-editor';
import {useMarginMarkers} from '../../../../../components/prosemirror-editor/hooks/useMarginMarkers';
import {
    focusMetaMarkStart,
    selectMetaMarkThread,
    setActiveMarginMarker,
} from '../../../../../components/prosemirror-editor/plugins/marginMarkersPlugin';
import {buildThreadId} from '../../../../../components/prosemirror-editor/yMaps/commentsYMap';
import {removeReferenceMark} from '../../../../../components/prosemirror-editor/yMaps/referencesYMap';
import {syncBlockClassificationFloorForReference} from '../../../../../components/prosemirror-editor/helpers/referenceClassificationFloor';
import useYDocStore from '../../../../../stores/useYDocStore';

export default function useMarginRailController(editorView, documentId) {
    const {markers, active, activeReferenceUid} = useMarginMarkers(editorView);
    const [threadSnapshots, setThreadSnapshots] = React.useState({});

    const activeRef = React.useRef(active);
    const threadSnapshotsRef = React.useRef(threadSnapshots);
    const editorViewRef = React.useRef(editorView);

    // Update refs synchronously so callbacks always read the latest values.
    activeRef.current = active;
    threadSnapshotsRef.current = threadSnapshots;
    editorViewRef.current = editorView;

    const handleMarkerClick = React.useCallback((marker) => {
        const view = editorViewRef.current;
        if (!view) return;

        if (!marker) {
            const currentActive = activeRef.current;
            if (currentActive?.type === 'comment' && currentActive?.uid) {
                const snapshot = threadSnapshotsRef.current[`${currentActive.type}:${currentActive.uid}`];
                if (snapshot?.isLoaded && snapshot?.count === 0) {
                    removeMetaMarkByUid(view, 'comment', currentActive.uid);
                }
            }

            setActiveMarginMarker(view, {type: null, uid: null});
            return;
        }

        setActiveMarginMarker(view, {type: marker.type, uid: marker.uid});
        if (marker.type === 'comment' || marker.type === 'reference') {
            focusMetaMarkStart(view, {type: marker.type, uid: marker.uid});
            return;
        }

        selectMetaMarkThread(view, {type: marker.type, uid: marker.uid});
    }, []);

    const handleThreadSnapshot = React.useCallback((snapshot) => {
        if (!snapshot?.uid || !snapshot?.type) return;
        setThreadSnapshots((prev) => ({...prev, [`${snapshot.type}:${snapshot.uid}`]: snapshot}));
    }, []);

    const handleMarkerDelete = React.useCallback((marker) => {
        const view = editorViewRef.current;
        if (!view || !marker?.uid || !marker?.type) return;

        const {ydoc, yComments} = useYDocStore.getState();

        const doDelete = () => {
            // Reference marks: delete the Y.Map entry AND sync the containing block's
            // classification level BEFORE stripping the ProseMirror mark from the text.
            // syncBlockClassificationFloorForReference locates the block by scanning the
            // CURRENT document for a text run still carrying this uid — once
            // removeMetaMarkByUid runs, that uid disappears from the doc and the block can
            // no longer be found, silently skipping the floor recompute (MINSKY).
            if (marker.type === 'reference') {
                const {yReferenceMarks} = useYDocStore.getState();
                if (yReferenceMarks) {
                    removeReferenceMark(ydoc, yReferenceMarks, marker.uid);
                    // Resulting level is manualBaseline ? max(manualBaseline, citationsFloor) :
                    // citationsFloor — see referenceClassificationFloor.js. Removal can only
                    // lower the level back to the block's manual baseline (or the citations
                    // floor if it was never manually set), never below it.
                    syncBlockClassificationFloorForReference(
                        view,
                        marker.uid,
                        documentId,
                        yReferenceMarks
                    );
                }
            }

            removeMetaMarkByUid(view, marker.type, marker.uid);

            const currentActive = activeRef.current;
            if (currentActive?.type === marker.type && currentActive?.uid === marker.uid) {
                setActiveMarginMarker(view, {type: null, uid: null});
            }

            if (marker.type === 'comment' && yComments) {
                const threadId = buildThreadId(marker.type, marker.uid);
                if (yComments.has(threadId)) {
                    yComments.delete(threadId);
                }
            }
        };

        if (ydoc) {
            ydoc.transact(doDelete, null);
        } else {
            doDelete();
        }

        view.focus();
    }, [documentId]);

    return {
        markers,
        active,
        activeReferenceUid,
        handleMarkerClick,
        handleMarkerDelete,
        handleThreadSnapshot,
    };
}

